import React from 'react';
import { REEBELO_TZ_NAME } from '@lambda/reebelo';
import { handleFilterReebeloCarePackages } from '@/lib/smartForm/orderUnitUtil';
import { formatDayJsDateShort } from '@/components/track-your-order/utils';
import settings from '@/settings';
import ShipmentDetail from './ShipmentDetail';
import {
  ShipmentLineItem,
  Shipment,
  CustomerOrder,
} from '@/lib/smartForm/types';
import RecommendedAccessories from '../products/RecommendedAccessories';

const OrderMeta = ({
  head,
  detail,
  widthClass,
  headSize = 'text-base',
}: {
  head: string;
  detail: string;
  headSize?: string;
  widthClass: string;
}) => (
  <div
    className={`flex ${widthClass} flex-row flex-wrap content-start items-start text-sm`}
  >
    <h2 className={`w-full ${headSize} font-bold leading-tight`}>{head}</h2>
    <span className="w-full text-sm leading-tight">{detail}</span>
  </div>
);

export default function OrderUnit(props: {
  order: CustomerOrder;
  index: number;
}) {
  const { order, index } = props;
  const { orderName, processedAt, shipments, totalPrice, lineItems } = order;

  // Extract a list of RebeeloCare title
  const reebeloCares = handleFilterReebeloCarePackages(shipments);
  /*
   * Below are normalized data for UI display
   */
  const { store } = settings;
  const storeTimeZone = REEBELO_TZ_NAME[store as keyof typeof REEBELO_TZ_NAME];
  const normalizedProcessData =
    processedAt !== null
      ? `Placed ${formatDayJsDateShort(processedAt, storeTimeZone)}`
      : '';
  const normalizedOrderNumber = `Order ${orderName}`;
  const normalizedTotalPrice =
    totalPrice !== null
      ? `${totalPrice?.currencyCode}$${Number(totalPrice?.amount).toFixed(2)}`
      : '';
  const {
    firstName,
    lastName,
    address1,
    city,
    country,
    province,
    zip,
    address2,
  } = order.shippingAddress;
  const normalizedShipTo = `${firstName} ${lastName}, ${address1}${
    address2 ? `, ${address2}` : ''
  }, ${city} ${province} ${zip}, ${country}`;

  return (
    <>
      <div className="mb-8 flex w-full flex-row flex-wrap">
        <div className="flow-root w-full rounded border border-gray-200 bg-gray-300 px-4 py-3.5 sm:flex">
          <div className="flex w-full pt-1 xs:pt-3 sm:w-1/2 sm:pt-0">
            <OrderMeta
              headSize="text-lg"
              widthClass="w-3/5 sm:gap-0 gap-1"
              head={normalizedOrderNumber}
              detail={normalizedProcessData}
            />
            <OrderMeta
              head="Total"
              detail={normalizedTotalPrice}
              widthClass="w-2/5 sm:gap-0 gap-1 text-right sm:text-justify"
            />
          </div>
          <div className="flex w-1/2 py-4 !pb-1 xs:py-8 xs:!pb-2 sm:py-0">
            <OrderMeta
              head="Ship To"
              detail={normalizedShipTo}
              widthClass="w-4/5 sm:gap-0 gap-1 hidden sm:block"
            />
            <OrderMeta
              head="Shipping to:"
              detail={normalizedShipTo}
              widthClass="w-4/5 sm:gap-0 gap-1 block sm:hidden"
            />
          </div>
        </div>
        {shipments
          .filter((shipment: Shipment) =>
            shipment.lineItems.some(
              (lineItem: ShipmentLineItem) =>
                !(
                  lineItem.name.toLowerCase().includes('reebelocare') ||
                  lineItem.name.toLowerCase().includes('express shipping')
                ),
            ),
          )
          .map((shipment: Shipment, idx: number) => (
            <ShipmentDetail
              key={idx}
              order={order}
              shipment={shipment}
              shipmentIndex={idx + 1}
              processedAt={processedAt}
              reebeloCares={reebeloCares}
              storeTimeZone={storeTimeZone}
              lineItems={lineItems ?? []}
            />
          ))}
      </div>
      {index === 0 && lineItems?.length && (
        <RecommendedAccessories tags={lineItems[0].tags ?? []} isAccount />
      )}
    </>
  );
}
